import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import pkceChallenge from 'pkce-challenge';

import { selectIsAuthenticated, selectProduct, userSelector } from 'stash/selectors';

import useNudata from 'stash/hooks/nudata';
import { generateRandomString } from 'stash/utils/random';
import { buildAuthRedirect } from 'stash/utils/helpers';
import { deleteCookie, getCookie, setCookie } from 'stash/utils/cookies';
import { getURLParam } from 'stash/utils';
import { PROMO_PARAM_NAME, PROMO_V2_PARAM_NAME } from 'stash/middleware/promo';
import {
	CODE_VERIFIER_COOKIE_NAME,
	PRODUCT_COOKIE_NAME,
	STATE_PARAM_COOKIE_NAME,
} from 'stash/constants/cookies';

const useAuthRedirect = () => {
	const history = useHistory();
	const [authRedirect, setAuthRedirect] = useState(null);
	const product = useSelector(selectProduct);
	const user = useSelector(userSelector);
	const isAuthenticated = useSelector(selectIsAuthenticated);
	const { nudataHasError, nudataQueryParams } = useNudata();

	useEffect(() => {
		if (isAuthenticated) {
			if (user?.created_at) {
				const cachedProduct = getCookie(PRODUCT_COOKIE_NAME);
				deleteCookie(PRODUCT_COOKIE_NAME);
				const defaultPath = 'phone-number';
				const productString = !product && cachedProduct ? '-' + cachedProduct : '';
				const redirectTo = `/${
					window.location.pathname.split('/')[1]
				}${productString}/${defaultPath}${window.location.search}`;
				return history.push(redirectTo);
			}
		} else {
			// ensure nudata session has been initialized before redirecting
			if (nudataQueryParams || nudataHasError) {
				//generate necessary authv2 security params
				const stateParam = generateRandomString();
				const { code_verifier, code_challenge } = pkceChallenge();

				//clear out any existing authv2 state param and set a new one in a cookie
				deleteCookie(STATE_PARAM_COOKIE_NAME);
				setCookie(STATE_PARAM_COOKIE_NAME, stateParam);

				//clear out any existing authv2 code verifier and set a new one in a cookie
				deleteCookie(CODE_VERIFIER_COOKIE_NAME);
				setCookie(CODE_VERIFIER_COOKIE_NAME, code_verifier);

				//save product in a cookie so that we can return to it after authV2 redirect
				if (product) {
					deleteCookie(PRODUCT_COOKIE_NAME);
					setCookie(PRODUCT_COOKIE_NAME, product);
				}

				const promoCode = getURLParam(PROMO_PARAM_NAME);
				const promoCodeV2 = getURLParam(PROMO_V2_PARAM_NAME);

				const authRedirect = buildAuthRedirect(
					window.Stash.env,
					stateParam,
					code_challenge,
					nudataQueryParams,
					promoCode,
					promoCodeV2,
					product
				);

				//redirect to Hodor for username creation
				setAuthRedirect(authRedirect);
			}
		}
	}, [
		isAuthenticated,
		nudataHasError,
		nudataQueryParams,
		product,
		history,
		user.created_at,
	]);

	return {
		authRedirect,
	};
};

export default useAuthRedirect;
